import { useEffect } from 'react';
import { signOut, useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';

import { showWarningToast } from '@/components/ui/sonner';
import type { UserData } from '@/store/profile.store';
import { useProfileStore } from '@/store/profile.store';

interface ProfileData {
  name: string;
  username: string;
  email?: string;
  bio?: string;
  instagramId?: string | null;
  vkId?: string | null;
  telegramId?: string | null;
  maxId?: string | null;
  donation?: {
    donationUserId?: string | null;
    hasCard: boolean;
  };
}

export const useProfile = () => {
  const { data: session } = useSession();
  const router = useRouter();

  // Get state and actions from Zustand store
  const userData = useProfileStore((state) => state.userData);
  const isLoading = useProfileStore((state) => state.isLoading);
  const isLoadingUser = useProfileStore((state) => state.isLoadingUser);
  const fetchUserDataFromStore = useProfileStore((state) => state.fetchUserData);
  const updateProfileInStore = useProfileStore((state) => state.updateProfile);
  const clearProfile = useProfileStore((state) => state.clearProfile);

  // Fetch user data when session changes
  useEffect(() => {
    const loadUserData = async () => {
      if (session?.user?.id && session.accessToken) {
        try {
          await fetchUserDataFromStore(session.accessToken, session.user.id);
        } catch (error) {
          console.error('Failed to fetch user data:', error);
          await signOut({ redirect: false });
          router.push('/');
        }
      } else if (!session?.user?.id) {
        // Clear profile when user logs out
        clearProfile();
      }
    };

    loadUserData();
  }, [session?.user?.id, session?.accessToken]);

  const fetchUserData = async (options?: { force?: boolean }) => {
    if (!session?.user?.id || !session.accessToken) return;

    try {
      await fetchUserDataFromStore(session.accessToken, session.user.id, options);
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      await signOut({ redirect: false });
      router.push('/');
    }
  };

  const updateProfile = async (data: ProfileData) => {
    if (!session?.user?.id) {
      showWarningToast('Пользователь не авторизован');
      return false;
    }

    if (!session.accessToken) {
      showWarningToast('Требуется авторизация');
      return false;
    }

    const success = await updateProfileInStore(data, session.accessToken, session.user.id);

    if (success) {
      // Update session with new data
      // Note: next-auth session update is handled separately if needed
      return true;
    }

    showWarningToast('Не удалось обновить профиль');
    return false;
  };

  return {
    updateProfile,
    isLoading,
    userData,
    isLoadingUser,
    fetchUserData,
  };
};

export type { UserData };
